import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { usePublicAnalytics } from './hooks/usePublicAnalytics';
import { AuthProvider } from './contexts/AuthContext';
import { SessionTimeoutWrapper } from './components/SessionTimeoutWrapper';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { PublicLayout } from './components/PublicLayout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { HomepagePage } from './pages/HomepagePage';
import { AboutPage } from './pages/AboutPage';
import { ServicesPage } from './pages/ServicesPage';
import { ContactPage } from './pages/ContactPage';
import { BookPage } from './pages/BookPage';
import { PatientListPage } from './pages/PatientListPage';
import { PatientRegisterPage } from './pages/PatientRegisterPage';
import { PatientProfilePage } from './pages/PatientProfilePage';
import { PublicRegisterPage } from './pages/PublicRegisterPage';
import { PendingRegistrationsPage } from './pages/PendingRegistrationsPage';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { BillingPage } from './pages/BillingPage';
import { CreateInvoicePage } from './pages/CreateInvoicePage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { ConsumablesPage } from './pages/ConsumablesPage';
import { Icd10ManagementPage } from './pages/Icd10ManagementPage';
import { NhrplManagementPage } from './pages/NhrplManagementPage';
import { ClinicalPage } from './pages/ClinicalPage';
import { FormularyPage } from './pages/FormularyPage';
import { WaitingListPage } from './pages/WaitingListPage';
import { WhatsAppPage } from './pages/WhatsAppPage';
import { ClaimsPage } from './pages/ClaimsPage';
import { ReportsPage } from './pages/ReportsPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { PatientInfoPage } from './pages/PatientInfoPage';
import { ReferPage } from './pages/ReferPage';
import { HospitalsPage } from './pages/HospitalsPage';
import { ProcedureTemplatesPage } from './pages/ProcedureTemplatesPage';
import { ProcedureLogbookPage } from './pages/ProcedureLogbookPage';
import { PregnancyListPage } from './pages/PregnancyListPage';
import { PregnancyDetailPage } from './pages/PregnancyDetailPage';
import { DeliveryLogbookPage } from './pages/DeliveryLogbookPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppLayout>{children}</AppLayout>
    </ProtectedRoute>
  );
}

function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  usePublicAnalytics();
  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <AnalyticsProvider>
      <AuthProvider>
        <SessionTimeoutWrapper>
          <ErrorBoundary>
          <Routes>
            {/* Public website routes — no auth required */}
            <Route path="/" element={<PublicLayout heroPage><HomepagePage /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/book" element={<PublicLayout><BookPage /></PublicLayout>} />
            <Route path="/patient-info" element={<PublicLayout><PatientInfoPage /></PublicLayout>} />
            <Route path="/refer" element={<PublicLayout><ReferPage /></PublicLayout>} />
            <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<PublicRegisterPage />} />

            {/* Authenticated portal routes */}
            <Route path="/dashboard" element={<AuthenticatedLayout><DashboardPage /></AuthenticatedLayout>} />
            <Route path="/patients" element={<AuthenticatedLayout><PatientListPage /></AuthenticatedLayout>} />
            <Route path="/patients/new" element={<AuthenticatedLayout><PatientRegisterPage /></AuthenticatedLayout>} />
            <Route path="/patients/:patientId" element={<AuthenticatedLayout><PatientProfilePage /></AuthenticatedLayout>} />
            <Route path="/pending-registrations" element={<AuthenticatedLayout><PendingRegistrationsPage /></AuthenticatedLayout>} />
            <Route path="/appointments" element={<AuthenticatedLayout><AppointmentsPage /></AuthenticatedLayout>} />
            <Route path="/billing" element={<AuthenticatedLayout><BillingPage /></AuthenticatedLayout>} />
            <Route path="/billing/new" element={<AuthenticatedLayout><CreateInvoicePage /></AuthenticatedLayout>} />
            <Route path="/billing/:invoiceId" element={<AuthenticatedLayout><InvoiceDetailPage /></AuthenticatedLayout>} />
            <Route path="/clinical/:patientId" element={<AuthenticatedLayout><ClinicalPage /></AuthenticatedLayout>} />
            <Route path="/patients/:patientId/pregnancies" element={<AuthenticatedLayout><PregnancyListPage /></AuthenticatedLayout>} />
            <Route path="/patients/:patientId/pregnancies/:pregId" element={<AuthenticatedLayout><PregnancyDetailPage /></AuthenticatedLayout>} />
            <Route path="/logbook" element={<AuthenticatedLayout><ProcedureLogbookPage /></AuthenticatedLayout>} />
            <Route path="/surgical-logbook" element={<Navigate to="/logbook" replace />} />
            <Route path="/delivery-logbook" element={<AuthenticatedLayout><DeliveryLogbookPage /></AuthenticatedLayout>} />
            <Route path="/waiting-list" element={<AuthenticatedLayout><WaitingListPage /></AuthenticatedLayout>} />
            <Route path="/whatsapp" element={<AuthenticatedLayout><WhatsAppPage /></AuthenticatedLayout>} />
            <Route path="/claims" element={<AuthenticatedLayout><ClaimsPage /></AuthenticatedLayout>} />
            <Route path="/reports" element={<AuthenticatedLayout><ReportsPage /></AuthenticatedLayout>} />
            <Route path="/settings/users" element={<AuthenticatedLayout><UserManagementPage /></AuthenticatedLayout>} />
            <Route path="/settings/formulary" element={<AuthenticatedLayout><FormularyPage /></AuthenticatedLayout>} />
            <Route path="/settings/consumables" element={<AuthenticatedLayout><ConsumablesPage /></AuthenticatedLayout>} />
            <Route path="/settings/icd10" element={<AuthenticatedLayout><Icd10ManagementPage /></AuthenticatedLayout>} />
            <Route path="/settings/nhrpl" element={<AuthenticatedLayout><NhrplManagementPage /></AuthenticatedLayout>} />
            <Route path="/settings/hospitals" element={<AuthenticatedLayout><HospitalsPage /></AuthenticatedLayout>} />
            <Route path="/settings/procedure-templates" element={<AuthenticatedLayout><ProcedureTemplatesPage /></AuthenticatedLayout>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </SessionTimeoutWrapper>
      </AuthProvider>
      </AnalyticsProvider>
    </BrowserRouter>
  );
}

export default App;
